import { useState } from "react";
import { Outlet, Navigate, useLocation } from "react-router";
import {
  useAuthSession,
  useAuthUser,
} from "@/components/providers/auth-context";
import { useProvisioning } from "@/lib/hooks/use-provisioning";
import { useIsNewAccount } from "@/lib/hooks/use-is-new-account";
import { useSignOut } from "@/lib/hooks/use-sign-out";
import { AccountGreeting, OnboardingHeader } from "@/components/onboarding";
import { FullScreenLoader } from "@/components/ui/loader";
import { isCheckoutReturn } from "./checkout-return";

export function OnboardingLayout() {
  const { isAuthenticated, isSessionLoading } = useAuthSession();
  const { isUserLoading } = useAuthUser();
  const { isProvisioned, isProvisioning, provisioningError, profile } =
    useProvisioning();
  const { pathname, search } = useLocation();
  const isNewAccount = useIsNewAccount(profile);
  const signOut = useSignOut();

  // A tenant that has had a site has onboarded (task 495), and the first run
  // is not for it any more: a bookmark or a stale tab must not offer it
  // again. Decided once, from the profile the layout first has. The first
  // run's own site sets `firstSiteCreatedAt`, and a refetch of the profile
  // while a refused trial waits for a choice (the client refetches on window
  // focus) must not pull the tenant out of it.
  const [onboardedOnEntry, setOnboardedOnEntry] = useState<boolean | null>(
    null,
  );
  if (profile && onboardedOnEntry === null) {
    setOnboardedOnEntry(Boolean(profile.tenant.firstSiteCreatedAt));
  }

  if (isSessionLoading || isUserLoading) {
    return <FullScreenLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isProvisioning) {
    return <FullScreenLoader />;
  }

  if (provisioningError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        <div className="text-destructive">Failed to set up your account</div>
        <div className="text-sm text-muted-foreground">
          {provisioningError.message}
        </div>
        <button
          onClick={() => window.location.reload()}
          className="text-sm text-primary underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!isProvisioned) {
    return <FullScreenLoader />;
  }

  // A checkout started from the old plan step, returning after the deploy
  // that removed it, is still verified there (`LegacyTierRoute`).
  if (onboardedOnEntry && !isCheckoutReturn(pathname, search)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      <OnboardingHeader />
      <main className="container mx-auto max-w-lg px-4 py-6 sm:py-10">
        {profile && (
          <div className="mb-6">
            <AccountGreeting
              isNewAccount={isNewAccount}
              email={profile.user.email}
              onSignOut={() => void signOut()}
            />
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
